import { Static, Type } from '@sinclair/typebox';
import { AssistantHistoryMessageSchema, LeadState, ToolInvocationStatus, TriggerRunStatus } from './assistant';
import { clearableOnUpdate } from './typebox-clearable';
import { ConversationPilot } from './crm';
import { ImpersonationScope } from './auth';
import { CalendarSyncProvider } from './translation_namespace/calendar_sync_namespace';
import { ProSubscriptionStatus, Subcription } from './namespace';
import { PlanSizeSchema, PlanCommitmentSchema } from './subscription';
import { MigrationV1BlockSchema } from './migration_v1';
import { ProVatRegime } from './vat';
import { NON_CONVERSION_NOTE_MAX_LENGTH, NonConversionReason } from './non-conversion';
import { AffiliateReferralReviewState } from './affiliate';
import { ONBOARDING_SECTOR_VALUES, type OnboardingSector } from './onboarding/sectoral-defaults';
import { SearchEnginePropertyStatus } from './translation_namespace/search_engine_property_namespace';

export enum AdminRole {
  // Legacy (≡ SUPER_ADMIN) — conservé pour les lignes historiques, mappé full dans ROLE_SCOPES.
  PROPELSIA_ADMIN = 'PROPELSIA_ADMIN',
  SUPER_ADMIN = 'SUPER_ADMIN',
  FINANCE = 'FINANCE',
  SUPPORT = 'SUPPORT',
  OPS = 'OPS',
  READ_ONLY = 'READ_ONLY',
  // Prestataire d'infogérance : supervision technique UNIQUEMENT (page Monitoring).
  // Aucun accès aux données business (CRM, finance, leads) ni aux outils.
  INFOGERANCE = 'INFOGERANCE',
  // Communications (campagnes, newsletter, séquence) : lit le CRM, n'y écrit rien d'autre.
  COMMUNICATION = 'COMMUNICATION',
}

/** Scopes fins protégeant endpoints + entrées de menu. cf. admin-cleanup-and-vision-plan §8.2. */
export enum AdminScope {
  // Finance transverse en lecture : liste des impayés de tous les pros (`billing/outstanding`).
  // MRR / cohortes / churn vivent dans Metabase, plus dans l'admin.
  FINANCE_READ = 'FINANCE_READ',
  // Facturation d'UN pro en lecture (impayés / relance de paiement sur la fiche pro), sans la
  // liste transverse (= FINANCE_READ) ni aucune action (= BILLING_GODMODE).
  BILLING_READ = 'BILLING_READ',
  CRM_READ = 'CRM_READ',
  CRM_WRITE = 'CRM_WRITE',
  // Communications du CRM : campagnes, newsletter, séquence automatique, audiences, plafond.
  COMMS_WRITE = 'COMMS_WRITE',
  BILLING_GODMODE = 'BILLING_GODMODE',
  CATALOG_WRITE = 'CATALOG_WRITE',
  DOMAINS_WRITE = 'DOMAINS_WRITE',
  MONITORING_READ = 'MONITORING_READ',
  AUDIT_READ = 'AUDIT_READ',
  TOOLS_DANGEROUS = 'TOOLS_DANGEROUS',
  ADMIN_USERS_MANAGE = 'ADMIN_USERS_MANAGE',
  // Impersonation d'un compte pro : READ = session lecture seule (lit tout ce que lit le pro,
  // n'écrit rien), WRITE = session contrôle total. cf. plan impersonate.
  IMPERSONATE_READ = 'IMPERSONATE_READ',
  IMPERSONATE_WRITE = 'IMPERSONATE_WRITE',
  // Affiliation v2 : READ = consultation (liste, fiche, stats), WRITE = gestion
  // (création, régime/statut, saisie manuelle de commissions, règlements).
  AFFILIATE_READ = 'AFFILIATE_READ',
  AFFILIATE_WRITE = 'AFFILIATE_WRITE',
  // Migration des boîtes mail OVH → Stalwart : READ = récap (qui, quand, où ça en est) pour le CSM,
  // WRITE = inventaire, préparer, planifier, migrer, resynchroniser, finaliser, annuler. WRITE
  // reste réservé aux super-admins (bascule DNS + reset des mots de passe OVH d'un pro).
  MAIL_MIGRATION_READ = 'MAIL_MIGRATION_READ',
  MAIL_MIGRATION_WRITE = 'MAIL_MIGRATION_WRITE',
}

const ALL_SCOPES: AdminScope[] = Object.values(AdminScope);

/**
 * Table de correspondance rôle → scopes (source de vérité unique partagée front+back).
 * Les scopes ne sont PAS stockés dans le JWT : ils sont dérivés du rôle via cette table.
 * (Préfixe ADMIN_ pour ne pas collisionner avec le `ROLE_SCOPES` côté pro de roles-config.ts.)
 */
export const ADMIN_ROLE_SCOPES: Record<AdminRole, AdminScope[]> = {
  // Tout, y compris outils dangereux + gestion des admins.
  [AdminRole.PROPELSIA_ADMIN]: ALL_SCOPES,
  [AdminRole.SUPER_ADMIN]: ALL_SCOPES,
  // Finance en lecture (impayés transverses), pas de god-mode. Affiliation en consultation ;
  // le règlement (AFFILIATE_WRITE, émet des codes promo) reste réservé aux super-admins.
  [AdminRole.FINANCE]: [
    AdminScope.FINANCE_READ,
    AdminScope.BILLING_READ,
    AdminScope.CRM_READ,
    AdminScope.MONITORING_READ,
    AdminScope.AUDIT_READ,
    AdminScope.AFFILIATE_READ,
  ],
  // CRM en lecture + actions douces, pas de god-mode abo ni outils destructifs.
  // Impersonation en lecture seule uniquement (support). Voit les impayés d'un pro (BILLING_READ)
  // mais ni la finance agrégée ni les leviers de facturation. Affiliation en consultation
  // (IBAN / SIRET masqués) ; valider, régler, rattacher restent en AFFILIATE_WRITE.
  [AdminRole.SUPPORT]: [
    AdminScope.CRM_READ,
    AdminScope.CRM_WRITE,
    AdminScope.COMMS_WRITE,
    AdminScope.BILLING_READ,
    AdminScope.MONITORING_READ,
    AdminScope.IMPERSONATE_READ,
    AdminScope.AFFILIATE_READ,
    // Le CSM lit le récap des migrations mail (qui prévenir, quand) ; les actions restent super-admin.
    AdminScope.MAIL_MIGRATION_READ,
  ],
  // Contenu & produit (catalogue, domaines) + monitoring. Impersonation lecture seule.
  [AdminRole.OPS]: [
    AdminScope.CATALOG_WRITE,
    AdminScope.DOMAINS_WRITE,
    AdminScope.MONITORING_READ,
    AdminScope.CRM_READ,
    AdminScope.IMPERSONATE_READ,
    AdminScope.MAIL_MIGRATION_READ,
  ],
  // Lecture seule partout, aucune mutation.
  [AdminRole.READ_ONLY]: [
    AdminScope.FINANCE_READ,
    AdminScope.BILLING_READ,
    AdminScope.CRM_READ,
    AdminScope.MONITORING_READ,
    AdminScope.AUDIT_READ,
    AdminScope.AFFILIATE_READ,
    AdminScope.MAIL_MIGRATION_READ,
  ],
  // Infogérance (prestataire technique externe) : strictement la supervision.
  // Volontairement SANS CRM_READ / FINANCE_READ — c'est ce qui réduit sa nav à Monitoring.
  [AdminRole.INFOGERANCE]: [AdminScope.MONITORING_READ],
  // Communications : voit les personnes du CRM pour composer une audience, n'écrit que les envois.
  [AdminRole.COMMUNICATION]: [AdminScope.CRM_READ, AdminScope.COMMS_WRITE],
};

/** Scopes effectifs d'un rôle admin (helper partagé front/back). */
export function adminScopesForRole(role: AdminRole): AdminScope[] {
  return ADMIN_ROLE_SCOPES[role] ?? [];
}

export function adminRoleHasScope(role: AdminRole, scope: AdminScope): boolean {
  return adminScopesForRole(role).includes(scope);
}

/**
 * Rôles dispensés de double authentification (décision équipe sécurité, 30/09/2026) : le
 * prestataire d'infogérance partage l'accès entre ses équipes d'astreinte, et ce rôle ne voit que
 * la supervision (MONITORING_READ). N'ajouter ici qu'un rôle sans aucun accès business ni écriture
 * — invariant verrouillé par `admin.spec.ts`. L'exemption est réévaluée sur le rôle COURANT à
 * chaque requête (`JwtAdminStrategy`) : un compte promu hors de ce rôle perd sa session.
 */
export const ADMIN_MFA_EXEMPT_ROLES: readonly AdminRole[] = [AdminRole.INFOGERANCE];

export function isAdminMfaExemptRole(role: AdminRole): boolean {
  return ADMIN_MFA_EXEMPT_ROLES.includes(role);
}

export enum AdminAuditAction {
  LOGIN = 'LOGIN',
  LEAD_VIEW = 'LEAD_VIEW',
  LEAD_LIST_VIEW = 'LEAD_LIST_VIEW',
  CONVERSATION_VIEW = 'CONVERSATION_VIEW',
  LEAD_STATE_OVERRIDE = 'LEAD_STATE_OVERRIDE',
  PLAYBOOK_EDIT = 'PLAYBOOK_EDIT',
  TRIGGER_EDIT = 'TRIGGER_EDIT',
  HANDOFF_TAKEOVER = 'HANDOFF_TAKEOVER',
  DEBUG_PREVIEW_CREATE = 'DEBUG_PREVIEW_CREATE',
  PROFESSIONAL_LIST_VIEW = 'PROFESSIONAL_LIST_VIEW',
  PROFESSIONAL_DETAIL_VIEW = 'PROFESSIONAL_DETAIL_VIEW',
  PROFESSIONAL_TRIAL_UPDATE = 'PROFESSIONAL_TRIAL_UPDATE',
  SUBSCRIPTION_SEGMENTS_REGENERATE = 'SUBSCRIPTION_SEGMENTS_REGENERATE',
  PROFESSIONAL_SUBSCRIPTION_OVERRIDE = 'PROFESSIONAL_SUBSCRIPTION_OVERRIDE',
  PROFESSIONAL_VAT_REGIME_UPDATE = 'PROFESSIONAL_VAT_REGIME_UPDATE',
  DOMAIN_LIST_VIEW = 'DOMAIN_LIST_VIEW',
  DOMAIN_DETAIL_VIEW = 'DOMAIN_DETAIL_VIEW',
  DOMAIN_RETRY = 'DOMAIN_RETRY',
  DOMAIN_RECONCILE = 'DOMAIN_RECONCILE',
  DOMAIN_TEARDOWN = 'DOMAIN_TEARDOWN',
  ACTIVITY_CATEGORY_CREATE = 'ACTIVITY_CATEGORY_CREATE',
  ACTIVITY_CATEGORY_UPDATE = 'ACTIVITY_CATEGORY_UPDATE',
  ACTIVITY_CATEGORY_DELETE = 'ACTIVITY_CATEGORY_DELETE',
  ACTIVITY_CREATE = 'ACTIVITY_CREATE',
  ACTIVITY_UPDATE = 'ACTIVITY_UPDATE',
  ACTIVITY_DELETE = 'ACTIVITY_DELETE',
  PROMO_CODE_CREATE = 'PROMO_CODE_CREATE',
  PROMO_CODE_UPDATE = 'PROMO_CODE_UPDATE',
  PROMO_CODE_GENERATE_FOR_PRO = 'PROMO_CODE_GENERATE_FOR_PRO',
  PROMO_REDEMPTION_REVOKE = 'PROMO_REDEMPTION_REVOKE',
  // Horloge virtuelle (outil de test preprod — neutralisée en prod).
  CLOCK_OVERRIDE = 'CLOCK_OVERRIDE',
  CLOCK_RUN_CRON = 'CLOCK_RUN_CRON',
  // Migration abonnement v1→v2 (page admin) : formule + domaines.
  SUBSCRIPTION_MIGRATION_V1_EXECUTE = 'SUBSCRIPTION_MIGRATION_V1_EXECUTE',
  SUBSCRIPTION_MIGRATION_V1_DOMAIN_EXECUTE = 'SUBSCRIPTION_MIGRATION_V1_DOMAIN_EXECUTE',
  // (Dés)activation du redirect legacy v1→v2 d'un pro depuis l'admin (écrit `v2_pro_id` côté v1).
  MIGRATION_REDIRECT_TOGGLE = 'MIGRATION_REDIRECT_TOGGLE',
  // Bascule finale v1→v2 (page admin migration-cutover) : wipe + réimport sélectif par bloc,
  // puis clôture (pose `v1_migration_completed_at`). `after` JSONB = blocs cochés + wipe.
  MIGRATION_CUTOVER_EXECUTE = 'MIGRATION_CUTOVER_EXECUTE',
  MIGRATION_CUTOVER_COMPLETE = 'MIGRATION_CUTOVER_COMPLETE',
  // Relink post-réimport : réparation des références aux ids catalogue régénérés
  // (instructions, champs perso, ressources, durées par membre, config du site).
  // Écrit par l'orchestrateur en fin de run, donc APRÈS `MIGRATION_CUTOVER_EXECUTE`.
  MIGRATION_CUTOVER_RELINK = 'MIGRATION_CUTOVER_RELINK',
  // Consultation de la fiche CRM 360 (lecture seule, auditée).
  CRM_360_VIEW = 'CRM_360_VIEW',
  // Gestion des utilisateurs admin (§9).
  ADMIN_USER_CREATE = 'ADMIN_USER_CREATE',
  ADMIN_USER_UPDATE = 'ADMIN_USER_UPDATE',
  ADMIN_USER_DEACTIVATE = 'ADMIN_USER_DEACTIVATE',
  ADMIN_USER_ROLE_CHANGE = 'ADMIN_USER_ROLE_CHANGE',
  ADMIN_USER_PASSWORD_RESET = 'ADMIN_USER_PASSWORD_RESET',
  // Prise de contrôle d'un compte pro (impersonation) : démarrage d'une session.
  IMPERSONATE_START = 'IMPERSONATE_START',
  // Relance des essais : pause / reprise / arrêt de la séquence, renvoi d'un message.
  TRIAL_RELANCE_CONTROL = 'TRIAL_RELANCE_CONTROL',
  TRIAL_RELANCE_RESEND = 'TRIAL_RELANCE_RESEND',
  // File des relances 1:1 (`trigger_run`) pilotée depuis l'admin.
  RELANCE_CANCEL = 'RELANCE_CANCEL',
  RELANCE_RETRY = 'RELANCE_RETRY',
  // Escalade : `HANDOFF_TAKEOVER` (déjà présent) rend la conversation à l'humain, celle-ci la
  // rend à Pulse.
  HANDOFF_RELEASE = 'HANDOFF_RELEASE',
  // Mutation effectuée PENDANT une session d'impersonation (traçabilité par-action,
  // en pratique mode FULL — le READ_ONLY bloque déjà les écritures). cf. ImpersonationAuditInterceptor.
  IMPERSONATE_ACTION = 'IMPERSONATE_ACTION',
  // « Réserver avec Google » : décision admin sur la demande d'un pro + trigger manuel du feed
  // (cf. google-feed-merge-v1-v2-plan.md D5). L'approbation est au niveau pro uniquement.
  GOOGLE_RESERVE_APPROVE = 'GOOGLE_RESERVE_APPROVE',
  GOOGLE_RESERVE_REJECT = 'GOOGLE_RESERVE_REJECT',
  GOOGLE_RESERVE_RUN_TRIGGER = 'GOOGLE_RESERVE_RUN_TRIGGER',
  // Consultation des factures d'un pro depuis l'admin (lecture seule, auditée).
  INVOICE_LIST_VIEW = 'INVOICE_LIST_VIEW',
  // Affiliation v2 : gestion des affiliés + ledger de commissions (cf. affiliation-v2-plan.md).
  AFFILIATE_CREATE = 'AFFILIATE_CREATE',
  AFFILIATE_UPDATE = 'AFFILIATE_UPDATE',
  /** Validation d'une auto-inscription (PENDING → ACTIVE) : le lien commence à attribuer. */
  AFFILIATE_APPROVE = 'AFFILIATE_APPROVE',
  AFFILIATE_COMMISSION_MANUAL_ENTRY = 'AFFILIATE_COMMISSION_MANUAL_ENTRY',
  /** Validation de gains retenus (REVIEW → PENDING) : ils deviennent payables. */
  AFFILIATE_COMMISSION_APPROVE = 'AFFILIATE_COMMISSION_APPROVE',
  /** Validation ou refus d'un parrainage (filleul) : débloque ou annule ses gains. */
  AFFILIATE_REFERRAL_REVIEW = 'AFFILIATE_REFERRAL_REVIEW',
  AFFILIATE_COMMISSION_SETTLE = 'AFFILIATE_COMMISSION_SETTLE',
  AFFILIATE_BOUNTY_REGENERATE = 'AFFILIATE_BOUNTY_REGENERATE',
  /**
   * Rattachement (ou détachement) manuel d'un compte pro à un affilié. Audité à part de
   * `AFFILIATE_UPDATE` : c'est ce lien qui désigne la cible d'une conversion cagnotte →
   * budget, donc où part l'argent d'un régime CLIENT_PRO.
   */
  AFFILIATE_LINK_PROFESSIONAL = 'AFFILIATE_LINK_PROFESSIONAL',
  /**
   * Rapatriement manuel des commissions FirstPromoter (cohabitation v1/v2). Audité
   * parce qu'un run crée des lignes de ledger : il faut pouvoir dire qui l'a lancé.
   */
  AFFILIATE_FIRSTPROMOTER_SYNC = 'AFFILIATE_FIRSTPROMOTER_SYNC',
  // Remboursement d'une facture depuis l'admin : rend l'argent (refund provider de paiement)
  // ET émet l'avoir comptable. `after` JSONB = montant remboursé + motif.
  INVOICE_REFUND = 'INVOICE_REFUND',
  // Réconciliation manuelle des suppressions calendrier (provider → Propelsia) déclenchée
  // depuis la fiche pro. Supprime les blocs externes disparus du calendrier distant.
  // `after` JSONB = connexions ciblées.
  CALENDAR_SYNC_RECONCILE = 'CALENDAR_SYNC_RECONCILE',
  // Rejeu manuel des prélèvements abo en échec (dunning) depuis l'admin — déclenche des
  // charges Stripe réelles. `after` JSONB = résultat du rejeu (tentées/réussies/recouvré).
  BILLING_RETRY_OUTSTANDING = 'BILLING_RETRY_OUTSTANDING',
  // Abandon du recouvrement automatique (« Arrêter les relances ») : plus de relance, de
  // rejeu planifié ni de gel J+60. `after` JSONB = état du dunning au moment de l'arrêt.
  BILLING_DUNNING_STOP = 'BILLING_DUNNING_STOP',
  // Réarmement du recouvrement automatique après un arrêt.
  BILLING_DUNNING_RESUME = 'BILLING_DUNNING_RESUME',
  // Résiliation immédiate pour impayé depuis la fiche pro (statut CANCELED + fin à maintenant).
  // `before`/`after` JSONB = statut et date de fin d'abonnement.
  BILLING_CANCEL_UNPAID = 'BILLING_CANCEL_UNPAID',
  /**
   * Remise à zéro du contexte d'un lead (conversations soft-delete, relances annulées,
   * qualification vidée hors attribution) — pour rejouer un parcours Pulse depuis un
   * même numéro / navigateur. Refusé sur un lead déjà converti en pro.
   */
  LEAD_RESET = 'LEAD_RESET',
  /** Bascule Cfixé → Propelsia marquée comme réalisée depuis l'admin (écrit le statut côté v1). */
  MIGRATION_BASCULE_VALIDATE = 'MIGRATION_BASCULE_VALIDATE',
  /**
   * Email de fin de bascule envoyé au pro depuis l'admin (bascule déjà réalisée). Nom historique :
   * l'envoi est désormais toujours manuel, découplé de la validation.
   */
  MIGRATION_BASCULE_RESEND_EMAIL = 'MIGRATION_BASCULE_RESEND_EMAIL',
  /** Rattachement manuel d'un filleul à un ambassadeur (fiche affilié ou fiche pro). */
  AFFILIATE_REFERRAL_ATTACH = 'AFFILIATE_REFERRAL_ATTACH',
  /** Question posée à Pulse admin sur un pro (une ligne par tour, lecture seule). */
  ADMIN_PULSE_ASK = 'ADMIN_PULSE_ASK',
  /** Saisie ou modification de la raison de non-conversion d'un essai (fiche pro). */
  PROFESSIONAL_NON_CONVERSION_UPDATE = 'PROFESSIONAL_NON_CONVERSION_UPDATE',
  /** Ouverture ou fermeture du pack référencement pour un compte (fiche pro). */
  PROFESSIONAL_SEO_PACK_UPDATE = 'PROFESSIONAL_SEO_PACK_UPDATE',
  /** Double authentification (TOTP) activée par l'admin lui-même. */
  MFA_ENROLL = 'MFA_ENROLL',
  /** Code de double authentification refusé (mauvais code, rejeu ou compte verrouillé). */
  MFA_CHALLENGE_FAILED = 'MFA_CHALLENGE_FAILED',
  /** Connexion via un code de secours (usage unique) à la place du code TOTP. */
  MFA_RECOVERY_CODE_USED = 'MFA_RECOVERY_CODE_USED',
  /** Double authentification d'un autre admin réinitialisée (téléphone perdu). */
  MFA_RESET = 'MFA_RESET',
  // CRM (suivi humain après essai) : registre de contact, tâches, file d'appels, notes.
  CRM_CONSENT_SET = 'CRM_CONSENT_SET',
  CRM_CONSENT_REVOKE = 'CRM_CONSENT_REVOKE',
  CRM_TASK_WRITE = 'CRM_TASK_WRITE',
  CRM_CALL_LOG = 'CRM_CALL_LOG',
  CRM_NOTE_WRITE = 'CRM_NOTE_WRITE',
  CRM_QUEUE_ENTRY_WRITE = 'CRM_QUEUE_ENTRY_WRITE',
  /** Injection du stock d'essais terminés dans la file d'appels (outil, à blanc par défaut). */
  CRM_QUEUE_STOCK_INJECT = 'CRM_QUEUE_STOCK_INJECT',
  /** Présence cochée à une séance de formation. */
  CRM_TRAINING_ATTENDANCE = 'CRM_TRAINING_ATTENDANCE',
  /** Export CSV d'une liste de personnes du CRM (borné, filtres consignés). */
  CRM_EXPORT = 'CRM_EXPORT',
  /** Résumé d'un dossier demandé à Pulse. */
  CRM_SUMMARY_ASK = 'CRM_SUMMARY_ASK',
  /** Numéro composé en un clic par la ligne d'un admin (hors file d'appels, numéro masqué). */
  CRM_TELEPHONY_DIAL = 'CRM_TELEPHONY_DIAL',
  /** Campagne ou étape de séquence du CRM créée, modifiée ou supprimée. */
  CRM_CAMPAIGN_WRITE = 'CRM_CAMPAIGN_WRITE',
  /** Campagne du CRM envoyée, programmée ou déprogrammée. */
  CRM_CAMPAIGN_SEND = 'CRM_CAMPAIGN_SEND',
  /** Séquence automatique du CRM activée, désactivée ou réglée. */
  CRM_SEQUENCE_WRITE = 'CRM_SEQUENCE_WRITE',
  /** Email de suite de séance (démo, formation) réglé dans « Campagnes ». */
  CRM_SESSION_FOLLOWUP_WRITE = 'CRM_SESSION_FOLLOWUP_WRITE',
  /** Modèle d'email de la bibliothèque créé, modifié ou supprimé. */
  CRM_EMAIL_TEMPLATE_WRITE = 'CRM_EMAIL_TEMPLATE_WRITE',
  /** Identité / contact d'un lead corrigés depuis l'admin (prénom, nom, email, téléphone). */
  LEAD_UPDATE = 'LEAD_UPDATE',
  /** Domaine retiré du site depuis l'admin (primaire compris), renouvellement coupé. */
  DOMAIN_DELETE = 'DOMAIN_DELETE',
  /** Relance de l'enrôlement d'un hôte chez les moteurs de recherche (GSC + Bing) depuis l'admin. */
  DOMAIN_SEARCH_ENGINES_RETRY = 'DOMAIN_SEARCH_ENGINES_RETRY',
  /** Expéditeur de campagnes sur le domaine du pro re-vérifié ou retiré depuis l'admin. */
  CAMPAIGN_SENDER_DOMAIN_WRITE = 'CAMPAIGN_SENDER_DOMAIN_WRITE',
  // Migration des boîtes mail OVH → Stalwart (cf. email-ovh-exchange-migration-plan §4.3).
  /** Inventaire OVH relancé. */
  MAIL_MIGRATION_INVENTORY = 'MAIL_MIGRATION_INVENTORY',
  /** Prérequis contrôlés + TTL abaissé (« Préparer »). */
  MAIL_MIGRATION_PREPARE = 'MAIL_MIGRATION_PREPARE',
  /** Date de bascule / note de pilotage posées. */
  MAIL_MIGRATION_SCHEDULE = 'MAIL_MIGRATION_SCHEDULE',
  /** Job de migration lancé (ou relancé depuis ERROR, `after.retry`). */
  MAIL_MIGRATION_LAUNCH = 'MAIL_MIGRATION_LAUNCH',
  /** Recopie incrémentale demandée. */
  MAIL_MIGRATION_RESYNC = 'MAIL_MIGRATION_RESYNC',
  /** Passage DONE à la main. */
  MAIL_MIGRATION_FINALIZE = 'MAIL_MIGRATION_FINALIZE',
  /** Retour arrière : DNS d'origine reposé, rows soft-deleted, mot de passe OVH tourné. */
  MAIL_MIGRATION_ROLLBACK = 'MAIL_MIGRATION_ROLLBACK',
}

// =====================================================================
// Auth — POST /admin/auth/login
// =====================================================================

export const AdminLoginBodySchema = Type.Object({
  email: Type.String({ format: 'email', maxLength: 255 }),
  password: Type.String({ minLength: 8, maxLength: 255 }),
});

export const AdminProfileSchema = Type.Object({
  admin_user_id: Type.Number(),
  email: Type.String(),
  name: Type.String(),
  role: Type.Enum(AdminRole),
  last_login_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'last_login_at', with_time: true } }),
  ),
  /** TOTP confirmé (`totp_enabled_at` non nul). Un rôle dispensé peut l'avoir configuré quand même. */
  mfa_enabled: Type.Optional(Type.Boolean()),
  mfa_enabled_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'totp_enabled_at' } }),
  ),
});

export const AdminLoginResponseSchema = Type.Object({
  access_token: Type.String(),
  admin_user: AdminProfileSchema,
});

// =====================================================================
// Auth — double authentification (TOTP), étape 2 du login
// =====================================================================

/**
 * Étape 2 attendue après un mot de passe valide : la double authentification est obligatoire
 * pour tous les rôles, le login ne délivre donc jamais de session directement.
 */
export enum AdminMfaState {
  /** TOTP déjà activé : saisir le code (ou un code de secours). */
  CHALLENGE = 'CHALLENGE',
  /** Pas encore de TOTP : inscription obligatoire avant tout accès. */
  ENROLL = 'ENROLL',
  /**
   * 2FA contournée (`ADMIN_MFA_BYPASS`, local / preprod uniquement, jamais en prod) : la session
   * est renvoyée directement par le login.
   */
  BYPASSED = 'BYPASSED',
  /** Rôle dispensé de 2FA (`ADMIN_MFA_EXEMPT_ROLES`) : la session est renvoyée directement par le login. */
  EXEMPT = 'EXEMPT',
}

export const AdminLoginStepResponseSchema = Type.Object({
  mfa_state: Type.Enum(AdminMfaState),
  // Jeton court (5 min) qui ne vaut QUE pour l'étape 2 — aucun accès aux routes admin.
  // Absent quand la 2FA est contournée (BYPASSED) ou non requise pour le rôle (EXEMPT).
  mfa_token: Type.Optional(Type.String()),
  // Session directe, uniquement quand la 2FA est contournée (BYPASSED) ou non requise (EXEMPT).
  access_token: Type.Optional(Type.String()),
  admin_user: Type.Optional(AdminProfileSchema),
});

export const AdminMfaTokenBodySchema = Type.Object({
  mfa_token: Type.String({ minLength: 1, maxLength: 2048 }),
});

export const AdminMfaEnrollStartResponseSchema = Type.Object({
  // URI standard `otpauth://totp/…` rendue en QR code côté admin-web.
  otpauth_uri: Type.String(),
  // Même secret en base32, pour une saisie manuelle dans l'application d'authentification.
  secret: Type.String(),
});

export const AdminMfaCodeBodySchema = Type.Object({
  mfa_token: Type.String({ minLength: 1, maxLength: 2048 }),
  // Code TOTP à 6 chiffres, ou code de secours `xxxx-xxxx` (vérification uniquement).
  code: Type.String({ minLength: 6, maxLength: 32 }),
});

export const AdminMfaEnrollConfirmResponseSchema = Type.Object({
  access_token: Type.String(),
  admin_user: AdminProfileSchema,
  // Affichés UNE seule fois (seuls leurs hachés sont stockés).
  recovery_codes: Type.Array(Type.String()),
});

// =====================================================================
// Lead list / detail / conversation
// =====================================================================

export const AdminLeadListItemSchema = Type.Object({
  lead_id: Type.Number(),
  professional_id: Type.Optional(Type.Number()),
  professional_name: Type.Optional(Type.String()),
  state: Type.Union([
    Type.Literal(LeadState.COLD),
    Type.Literal(LeadState.WARM),
    Type.Literal(LeadState.QUALIFIED),
    Type.Literal(LeadState.SUSPENS),
    Type.Literal(LeadState.LOST),
    Type.Literal(LeadState.ONBOARDING),
    Type.Literal(LeadState.TRIAL),
    Type.Literal(LeadState.PAID),
    Type.Literal(LeadState.ACTIVE),
    Type.Literal(LeadState.AT_RISK),
    Type.Literal(LeadState.CHURNED),
    Type.Literal(LeadState.AMBASSADEUR),
    Type.Literal(LeadState.DISTANT_PROJECT),
  ]),
  current_playbook: Type.Optional(Type.String()),
  scoring: Type.Number(),
  source: Type.Optional(Type.String()),
  last_seen_channel: Type.Optional(Type.String()),
  phone_e164: Type.Optional(Type.String()),
  email: Type.Optional(Type.String()),
  /** Identité de la personne, saisie depuis l'admin (Pulse ne capte que le nom du commerce). */
  firstname: Type.Optional(Type.String()),
  lastname: Type.Optional(Type.String()),
  /**
   * Une conversation ouverte du lead est en pilotage HUMAIN (escaladée par Pulse ou prise en
   * main depuis l'inbox) : badge « Escaladé » dans la liste / la fiche. Dérivé de
   * `conversation.pilot`, la même source que l'inbox `/escalations` — pas de
   * `qualification_status`, qui n'est jamais remis à zéro à la remise de main.
   */
  human_pilot: Type.Optional(Type.Boolean()),
  created_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'created_at', with_time: true } }),
  updated_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'updated_at', with_time: true } }),
  /** Projet lointain (quiz 2 bis) : mois de lancement visé, formaté par le backend. */
  project_date_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'project_date' } })),
});

export const AdminLeadListResponseSchema = Type.Object({
  items: Type.Array(AdminLeadListItemSchema),
  total: Type.Number(),
  page: Type.Number(),
  per_page: Type.Number(),
});

export const AdminLeadDetailSchema = Type.Composite([
  AdminLeadListItemSchema,
  Type.Object({
    qualification: Type.Record(Type.String(), Type.Any()),
    conversation_count: Type.Number(),
  }),
]);

/**
 * Correction de l'identité / du contact d'un lead depuis l'admin. Un champ absent est inchangé ;
 * `null` ou chaîne vide l'efface. Le téléphone est normalisé en E.164 côté serveur (« 06… » accepté).
 */
export const AdminLeadUpdateBodySchema = Type.Object({
  firstname: Type.Optional(Type.Union([Type.String({ maxLength: 255 }), Type.Null()])),
  lastname: Type.Optional(Type.Union([Type.String({ maxLength: 255 }), Type.Null()])),
  email: Type.Optional(Type.Union([Type.String({ maxLength: 255 }), Type.Null()])),
  phone_e164: Type.Optional(Type.Union([Type.String({ maxLength: 32 }), Type.Null()])),
});

export const AdminToolInvocationSchema = Type.Object({
  tool_invocation_id: Type.Number(),
  tool_name: Type.String(),
  status: Type.Union([
    Type.Literal(ToolInvocationStatus.SUCCESS),
    Type.Literal(ToolInvocationStatus.ERROR),
    Type.Literal(ToolInvocationStatus.REFUSED),
  ]),
  duration_ms: Type.Optional(Type.Number()),
  error: Type.Optional(Type.String()),
  created_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'created_at', with_time: true } }),
});

export const AdminConversationMessageSchema = Type.Composite([
  AssistantHistoryMessageSchema,
  Type.Object({
    tool_invocations: Type.Optional(Type.Array(AdminToolInvocationSchema)),
  }),
]);

export const AdminLeadConversationResponseSchema = Type.Object({
  lead_id: Type.Number(),
  conversation_id: Type.Optional(Type.Number()),
  active_playbook: Type.Optional(Type.String()),
  messages: Type.Array(AdminConversationMessageSchema),
  total: Type.Number(),
  page: Type.Number(),
  per_page: Type.Number(),
});

// =====================================================================
// Dashboard stats — GET /admin/dashboard/stats
// =====================================================================

/** Sévérité d'un bloc de récap : c'est elle qui décide la couleur, jamais l'écran. */
export enum AdminDashboardSeverity {
  OK = 'OK',
  WARN = 'WARN',
  ALERT = 'ALERT',
}

/**
 * Récap « coup d'œil » du tableau de bord admin. Volontairement RÉDUIT à des compteurs : l'analyse
 * (funnel de cycle de vie, cohortes, séries) vit dans Metabase, qui sait la faire mieux. Ici on
 * répond à une seule question — « est-ce que quelque chose demande mon attention maintenant ? ».
 */
export const AdminDashboardStatsResponseSchema = Type.Object({
  /** Fenêtre d'observation des compteurs datés, en jours. */
  window_days: Type.Number(),

  /** Où en est le parc client. */
  clients: Type.Object({
    /** Abonnements en cours, tous millésimes confondus. */
    active: Type.Number(),
    /** Comptes en période d'essai (essai commencé, non expiré, sans abonnement). */
    trials: Type.Number(),
    /** Paiement en défaut (`payment_at_risk` ou `PAST_DUE`) — churn imminent. */
    at_risk: Type.Number(),
    /** Inscriptions sur la fenêtre. */
    new_window: Type.Number(),
    /** Inscrits de la fenêtre devenus payants. */
    converted_window: Type.Number(),
    /** Abonnements résiliés sur la fenêtre. */
    churned_window: Type.Number(),
    severity: Type.Enum(AdminDashboardSeverity),
  }),

  /** Répartition des leads par état — seuls les états non vides sont renvoyés. */
  leads: Type.Object({
    total: Type.Number(),
    by_state: Type.Record(Type.String(), Type.Number()),
  }),

  /** Les deux files qui attendent une action humaine. */
  relances: Type.Object({
    /** `trigger_run` PENDING dont l'échéance est encore devant. */
    pending: Type.Number(),
    /** PENDING dont l'échéance est dépassée : le worker aurait dû l'envoyer. */
    late: Type.Number(),
    /** Échecs définitifs (3 tentatives épuisées). */
    failed: Type.Number(),
    /** Envoyées sur les dernières 24 h — preuve que la chaîne tourne. */
    sent_24h: Type.Number(),
    /** Conversations en pilotage humain non rendues. */
    escalations_open: Type.Number(),
    severity: Type.Enum(AdminDashboardSeverity),
  }),

  /**
   * Santé technique réduite à quatre compteurs. La page Monitoring garde le détail ; ce bloc ne
   * sert qu'à savoir s'il faut y aller.
   */
  monitoring: Type.Object({
    /** Jobs en échec, toutes queues BullMQ confondues. */
    queue_failed: Type.Number(),
    /** Providers externes dont le disjoncteur n'est pas fermé. */
    providers_degraded: Type.Number(),
    /** Crons dont le dernier passage a échoué. */
    crons_failed: Type.Number(),
    /** Erreurs API externes + notifications en échec sur 24 h. */
    errors_24h: Type.Number(),
    /**
     * `true` quand les compteurs portés par Redis (queues, disjoncteurs) n'ont pas pu être lus.
     * Les compteurs concernés valent alors 0 : sans ce drapeau, une panne Redis afficherait un
     * tableau de bord tout vert.
     */
    degraded: Type.Boolean(),
    severity: Type.Enum(AdminDashboardSeverity),
  }),

  /** Runtime Pulse — supervision du moteur conversationnel. */
  pulse: Type.Object({
    conversations: Type.Number(),
    messages_24h: Type.Number(),
    /** Appels d'outils en erreur sur 24 h. */
    tool_errors_24h: Type.Number(),
  }),
});

// =====================================================================
// Professionals (clients) — list / detail / edit
//
// `access_state` est la valeur dérivée par `computeAccessState()` (jamais
// stockée). Les montants sont en unités mineures côté DB et renvoyés
// pré-formatés via `transformToSchema` (meta.is_price). Les dates suivent la
// règle date : champ `_formatted` + `source_key`.
// =====================================================================

export const AdminProfessionalListItemSchema = Type.Object({
  professional_id: Type.Number(),
  name: Type.String(),
  email_contact: Type.Optional(Type.String()),
  /** Valeur de l'enum AccessState calculée côté service. */
  access_state: Type.String(),
  /** Plan courant (enum Subscription) ou absent si l'essai n'a jamais été converti. */
  subscription_label: Type.Optional(Type.String()),
  /** Statut de l'abonnement (enum ProSubscriptionStatus) ou absent. */
  subscription_status: Type.Optional(Type.String()),
  /** Taille de l'abonnement (enum ProSubscriptionSize) ou absent. */
  size: Type.Optional(Type.String()),
  trial_end_date_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'trial_end_date' } })),
  created_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'created_at', with_time: true } }),
  /** Régime de TVA appliqué à ce qu'on facture au pro (enum ProVatRegime). */
  vat_regime: Type.String(),
  /** Origine de l'entreprise (enum ProfessionalOrigin) : inscription ou entreprise supplémentaire. */
  origin: Type.String(),
});

export const AdminProfessionalListResponseSchema = Type.Object({
  items: Type.Array(AdminProfessionalListItemSchema),
  total: Type.Number(),
  page: Type.Number(),
  per_page: Type.Number(),
});

/**
 * Charge ponctuelle de prorata rattachée à un segment (billing_event CHARGE
 * `charge:prorata:…`). N'est PAS un segment : c'est un complément facturé lors d'un upgrade
 * (delta plan) ou d'une activation d'addon en cours de période. Affichée en sous-ligne sous
 * le segment parent dans la vue admin god-mode.
 */
export const AdminSegmentProrataChargeSchema = Type.Object({
  billing_event_id: Type.Number(),
  /** 'plan' (delta d'upgrade) ou 'addon' (prorata d'option). */
  kind: Type.String(),
  /** Code de l'addon quand `kind === 'addon'`. */
  addon_code: Type.Optional(Type.String()),
  /** Statut brut du billing_event : DONE | PENDING | FAILED. */
  status: Type.String(),
  /** true si le prorata est effectivement débité (status DONE). */
  is_billed: Type.Boolean(),
  amount_formatted: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'amount_minor' } })),
  created_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'created_at', with_time: true } }),
  ),
});

export const AdminSubscriptionSegmentSchema = Type.Object({
  pro_subscription_segment_id: Type.Number(),
  subscription_label: Type.String(),
  is_trial: Type.Boolean(),
  is_cancelable: Type.Boolean(),
  /** true si une charge Stripe DONE existe → segment verrouillé en édition. */
  is_billed: Type.Boolean(),
  discount_percentage: Type.Number(),
  /** ISO brut (logique client : comparer aux bornes éditables). */
  start: Type.String(),
  end: Type.String(),
  start_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'start' } })),
  end_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'end' } })),
  amount_formatted: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'amount' } })),
  amount_with_discount_formatted: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'amount_with_discount' } }),
  ),
  /** Charges prorata (upgrade / addon) rattachées à ce segment. Vide pour la plupart. */
  prorata_charges: Type.Optional(Type.Array(AdminSegmentProrataChargeSchema)),
});

export const AdminProfessionalSubscriptionSchema = Type.Object({
  subscription_label: Type.String(),
  status: Type.String(),
  size: Type.String(),
  commitment: Type.String(),
  billing_period: Type.String(),
  payment_at_risk: Type.Boolean(),
  is_white_label: Type.Boolean(),
  is_smart_ride: Type.Boolean(),
  is_mobile_app_whitelabel: Type.Boolean(),
  amount_formatted: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'amount' } })),
  subscription_end_date_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'subscription_end_date' } }),
  ),
  commitment_end_date_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'subscription_commitment_end_date' } }),
  ),
  next_renewal_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'next_renewal_at', with_time: true } }),
  ),
});

export const AdminProfessionalQuotaSchema = Type.Object({
  quota_email_left: Type.Number(),
  monthly_quota_email: Type.Number(),
  quota_sms_left: Type.Number(),
  monthly_quota_sms: Type.Number(),
  credit_email_left: Type.Number(),
  credit_sms_left: Type.Number(),
  reset_cycle_anchor_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'reset_cycle_anchor' } }),
  ),
});

/**
 * État TVA d'un pro pour la fiche admin. `regime` est la décision ; `rate_percent` en découle
 * (sauf régime non tranché, où il reste gelé sur la valeur héritée) ; `suggested_regime` n'est
 * qu'une aide à la décision dérivée du pays, jamais appliquée automatiquement.
 */
export const AdminProfessionalVatSchema = Type.Object({
  regime: Type.String(),
  /** Taux effectif appliqué, décimal possible (8,5). */
  rate_percent: Type.Number(),
  /** true = régime non tranché : la facturation de ce pro est bloquée. */
  needs_review: Type.Boolean(),
  /** Régime proposé d'après le pays d'adresse, absent si aucune adresse exploitable. */
  suggested_regime: Type.Optional(Type.String()),
  /** Pays de l'adresse de facturation (ou d'établissement à défaut). */
  country_code: Type.Optional(Type.String()),
  /** N° de TVA intracommunautaire déclaré par le pro (requis pour justifier une autoliquidation UE). */
  vat_number: Type.Optional(Type.String()),
  /** Mention légale qui sera imprimée sur ses factures, absente si le régime n'en impose aucune. */
  legal_mention: Type.Optional(Type.String()),
});

/** Corps du choix de régime depuis la fiche admin. */
export const AdminProfessionalVatRegimeUpdateBodySchema = Type.Object({
  regime: Type.Enum(ProVatRegime),
  /**
   * true = répercuter le nouveau taux sur l'abonnement et ses segments NON facturés. À laisser
   * activé dans le cas normal : sans ça, la décision ne prendrait effet qu'au cycle suivant.
   * Les segments déjà facturés ne sont jamais touchés (l'argent est pris, la facture est émise).
   */
  resync_open_segments: Type.Optional(Type.Boolean()),
});
export type AdminProfessionalVatRegimeUpdateBody = Static<typeof AdminProfessionalVatRegimeUpdateBodySchema>;

/**
 * Raison de non-conversion d'un essai (A3), saisie par l'équipe après un appel. Absente tant
 * que personne n'a rien renseigné.
 */
export const AdminProfessionalNonConversionSchema = Type.Object({
  reason: Type.Optional(Type.Enum(NonConversionReason)),
  note: Type.Optional(Type.String()),
  updated_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'updated_at', with_time: true } }),
  ),
});
export type AdminProfessionalNonConversion = Static<typeof AdminProfessionalNonConversionSchema>;

/** Corps de la saisie depuis la fiche admin. `null` efface la valeur. */
export const AdminProfessionalNonConversionUpdateBodySchema = Type.Object({
  reason: Type.Union([Type.Enum(NonConversionReason), Type.Null()]),
  note: Type.Optional(Type.Union([Type.String({ maxLength: NON_CONVERSION_NOTE_MAX_LENGTH }), Type.Null()])),
});
export type AdminProfessionalNonConversionUpdateBody = Static<typeof AdminProfessionalNonConversionUpdateBodySchema>;

/**
 * Pack référencement d'un pro (fiche admin). Tant que `SEO_ARTICLES_OPEN_TO_ALL` est faux en prod,
 * le pack s'ouvre compte par compte (`professional.seo_articles_enabled_at`) ; le plan MAX reste
 * exigé à part, sauf dérogation « hors Max » (`professional.seo_articles_any_plan_at`).
 * `onboarded_at` = le pro a suivi le parcours de première activation.
 */
export const AdminProfessionalSeoPackSchema = Type.Object({
  /** Ouvert à tous par l'environnement : l'interrupteur par compte n'a alors plus d'effet. */
  open_to_all: Type.Boolean(),
  enabled: Type.Boolean(),
  enabled_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'enabled_at', with_time: true } }),
  ),
  /** Formule actuelle MAX (ou MAX encore en vigueur jusqu'à la fin de la période payée). */
  plan_is_max: Type.Boolean(),
  /**
   * MAX encore payé mais quitté (changement de formule programmé, U11) : le pack se fermera à
   * `effective_at` sans dérogation, qu'on peut donc poser dès maintenant.
   */
  plan_max_ending: Type.Boolean(),
  /** Dérogation « hors Max » : le pack s'ouvre avec n'importe quelle formule payante en cours. */
  any_plan: Type.Boolean(),
  any_plan_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'any_plan_at', with_time: true } }),
  ),
  onboarded_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'onboarded_at', with_time: true } }),
  ),
});
export type AdminProfessionalSeoPack = Static<typeof AdminProfessionalSeoPackSchema>;

/** Mise à jour partielle : ouvrir/fermer le pack, et/ou poser/retirer la dérogation « hors Max ». */
export const AdminProfessionalSeoPackUpdateBodySchema = Type.Object({
  enabled: Type.Optional(Type.Boolean()),
  any_plan: Type.Optional(Type.Boolean()),
});
export type AdminProfessionalSeoPackUpdateBody = Static<typeof AdminProfessionalSeoPackUpdateBodySchema>;

/** Parrain du pro (programme ambassadeur) — absent si le pro n'a pas été parrainé. */
export const AdminProfessionalReferralSchema = Type.Object({
  affiliate_id: Type.Number(),
  email: Type.String(),
  first_name: Type.Optional(Type.String()),
  last_name: Type.Optional(Type.String()),
  ref_token: Type.String(),
  review_state: Type.Optional(Type.Enum(AffiliateReferralReviewState)),
  /** 1er encaissement commissionné — absent tant que le filleul n'a pas payé. */
  referred_at_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'referred_at' } })),
});

export const AdminProfessionalDetailSchema = Type.Composite([
  AdminProfessionalListItemSchema,
  Type.Object({
    /** Parrain (ambassadeur) qui a apporté ce pro, si le parrainage est attribué. */
    referral: Type.Optional(AdminProfessionalReferralSchema),
    legal_name: Type.Optional(Type.String()),
    currency: Type.String(),
    locale: Type.String(),
    timezone: Type.String(),
    stripe_customer_id: Type.Optional(Type.String()),
    /** Compte connecté du pro (encaissement de SES clients) — distinct du customer ci-dessus. */
    stripe_connect_account_id: Type.Optional(Type.String()),
    /** Fiche `company_customers` Pennylane. Nul tant que le pro n'a pas eu de facture Pennylane. */
    pennylane_customer_id: Type.Optional(Type.String()),
    /** Bloc TVA : ce qu'on facture AU pro (sans rapport avec la TVA qu'il applique à ses clients). */
    vat: AdminProfessionalVatSchema,
    /** ISO brut, pour pré-remplir le champ date du formulaire d'édition. */
    trial_end_date: Type.Optional(Type.String()),
    trial_started_at_formatted: Type.Optional(
      Type.String({ format: 'date-time', meta: { source_key: 'trial_started_at', with_time: true } }),
    ),
    subscription: Type.Optional(AdminProfessionalSubscriptionSchema),
    quota: Type.Optional(AdminProfessionalQuotaSchema),
    segments: Type.Array(AdminSubscriptionSegmentSchema),
    /** Pourquoi l'essai ne s'est pas transformé (saisie équipe, A3). */
    non_conversion: AdminProfessionalNonConversionSchema,
    /** Pack référencement : ouvert ou non pour ce compte, parcours d'activation suivi ou non. */
    seo_pack: AdminProfessionalSeoPackSchema,
  }),
]);

// ── Site vitrine d'un pro (onglet « Technique » de la fiche admin) ───────────────

/** Une boîte mail Stalwart rattachée au domaine (lecture seule côté admin). */
export const AdminDomainMailboxSchema = Type.Object({
  pro_mailbox_id: Type.Number(),
  email: Type.String(),
  display_name: Type.Optional(Type.String()),
  status: Type.String(),
  last_error: Type.Optional(Type.String()),
});

/**
 * État d'un hôte servi chez UN moteur de recherche (`pro_search_engine_property`, plan Bing D8).
 * `NONE` quand aucune ligne n'existe encore pour l'hôte : la réconciliation hebdomadaire la crée.
 */
export const AdminSearchEnginePropertySchema = Type.Object({
  status: Type.Enum(SearchEnginePropertyStatus),
  /** `sc-domain:<hôte>` (GSC) ou `https://<hôte>/` (Bing). */
  property_ref: Type.Optional(Type.String()),
  attempts: Type.Number(),
  verified_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'verified_at', with_time: true } }),
  ),
  last_checked_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'last_checked_at', with_time: true } }),
  ),
  last_error: Type.Optional(Type.String()),
});

/** Les deux moteurs suivis pour un hôte. Jamais montré au pro. */
export const AdminSearchEnginesSchema = Type.Object({
  gsc: AdminSearchEnginePropertySchema,
  bing: AdminSearchEnginePropertySchema,
});

export const AdminProfessionalWebsiteDomainSchema = Type.Object({
  domain: Type.String(),
  is_primary: Type.Boolean(),
  dns_status: Type.String(),
  activation_status: Type.String(),
  /** Statut du domaine mail Stalwart — absent = pas de messagerie configurée sur ce domaine. */
  mail_status: Type.Optional(Type.String()),
  mail_last_error: Type.Optional(Type.String()),
  mailboxes: Type.Array(AdminDomainMailboxSchema),
  search_engines: AdminSearchEnginesSchema,
});

/**
 * État du site d'un pro, pour diagnostiquer vite « pourquoi son site ne marche pas » :
 * publié ou non, quelle URL sert réellement, et où en sont DNS/activation de chaque domaine.
 */
export const AdminProfessionalWebsiteSchema = Type.Object({
  /** false = aucun contenu (ni brouillon ni publié) : le site n'a jamais été construit. */
  has_website: Type.Boolean(),
  is_published: Type.Boolean(),
  /** URL réellement servie : domaine primaire s'il existe, sinon le sous-domaine par défaut. */
  site_url: Type.String(),
  default_subdomain_url: Type.String(),
  last_published_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'last_published_at', with_time: true } }),
  ),
  /** Écart brouillon ↔ publié : > 0 = le pro voit dans le builder autre chose que ses visiteurs. */
  unpublished_changes_count: Type.Number(),
  domains: Type.Array(AdminProfessionalWebsiteDomainSchema),
  /** Enrôlement du sous-domaine par défaut (toujours servi, même avec un domaine custom). */
  default_subdomain_search_engines: AdminSearchEnginesSchema,
});

/**
 * Une connexion calendrier d'un membre d'équipe qui IMPORTE depuis un provider — donc
 * susceptible de détenir des blocs externes fantômes (event supprimé côté Google mais
 * toujours affiché dans l'agenda Propelsia).
 */
export const AdminCalendarSyncConnectionSchema = Type.Object({
  team_member_id: Type.Number(),
  team_member_name: Type.String(),
  provider: Type.Enum(CalendarSyncProvider),
  /** Calendrier réellement synchronisé (souvent un calendrier secondaire, pas le principal). */
  active_calendar_id: Type.Union([Type.String(), Type.Null()]),
  last_sync_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'last_sync_at', with_time: true } }),
  ),
  /** Blocs externes actuellement en base pour cette connexion (ordre de grandeur du diff à venir). */
  external_block_count: Type.Number(),
  last_reconcile_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'last_reconcile_at', with_time: true } }),
  ),
  /** Blocs fantômes supprimés lors du dernier passage. */
  last_reconcile_removed_count: Type.Union([Type.Number(), Type.Null()]),
  /**
   * Non nul = le plafond de sécurité a refusé la suppression de ce nombre d'orphelins.
   * La connexion ne se réconciliera plus toute seule tant qu'un humain n'a pas tranché :
   * c'est le seul état de cet écran qui appelle une action.
   */
  last_reconcile_blocked_count: Type.Union([Type.Number(), Type.Null()]),
});

export const AdminCalendarSyncConnectionsResponseSchema = Type.Object({
  connections: Type.Array(AdminCalendarSyncConnectionSchema),
});

/**
 * Déclenche une passe de réconciliation des suppressions provider → Propelsia.
 * `team_member_id` omis = toutes les connexions importantes du pro.
 */
export const AdminCalendarSyncReconcileBodySchema = Type.Object({
  team_member_id: Type.Optional(Type.Number()),
});

export const AdminCalendarSyncReconcileResponseSchema = Type.Object({
  /** Nombre de connexions pour lesquelles une réconciliation a été enfilée. */
  enqueued_count: Type.Number(),
});

/** Override god-mode de la date de fin d'essai (null = retirer l'essai). */
export const AdminProfessionalTrialUpdateBodySchema = Type.Object({
  trial_end_date: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
});

/**
 * Régénère les segments futurs via SegmentSchedulerService à partir de `from_date`.
 * `from_date` doit être >= début du premier segment non facturé (sinon rejet).
 */
export const AdminSubscriptionSegmentsRegenerateBodySchema = Type.Object({
  from_date: Type.String({ format: 'date-time' }),
  trial_days_remaining: Type.Optional(Type.Union([Type.Number(), Type.Null()])),
});

/**
 * Override god-mode de l'abonnement d'un pro pour tester le gating d'accès.
 *
 * ⚠️ Écriture directe en base : AUCUN appel Stripe, aucune facturation réelle.
 * Le couple (plan, flags addon, statut) pilote les scopes du JWT du pro à son
 * prochain refresh. `sync_quota` réécrit `pro_quota` depuis la définition du plan.
 * `size`/`commitment` suivent les enums Prisma (`SOLO`/`TEAM`, `MONTHLY`/`COMMITTED_12M`).
 */
export const AdminProfessionalSubscriptionUpdateBodySchema = Type.Object({
  subscription_label: Type.Enum(Subcription),
  size: PlanSizeSchema,
  commitment: PlanCommitmentSchema,
  status: Type.Enum(ProSubscriptionStatus),
  is_white_label: Type.Boolean(),
  is_smart_ride: Type.Boolean(),
  is_mobile_app_whitelabel: Type.Boolean(),
  /** Resynchronise `pro_quota` (SMS/email) sur les plafonds du plan cible. */
  sync_quota: Type.Boolean(),
  /**
   * God-mode (test) : date de début du 1er segment (`YYYY-MM-DD`), ancre de régénération.
   * Permet de placer le segment courant dans le passé pour que « maintenant » tombe en milieu
   * de cycle et tester les proratas d'upgrade. Optionnel — sinon ancré sur le 1er segment non
   * facturé (comportement par défaut). Jamais appliqué avant la fin d'un segment déjà facturé.
   * Format `date` (pas `date-time`) pour ne pas déclencher la conversion timezone de @TypeBoxBody.
   */
  first_segment_start_date: Type.Optional(Type.String()),
});

// =====================================================================
// Impersonation (prise de contrôle d'un compte pro)
//   POST /app/admin/professionals/:id/impersonate   (admin, scopé IMPERSONATE_*)
//   POST /app/public/professional/impersonate/exchange (public, échange du code)
//
// Le démarrage renvoie un `code` à usage unique (TTL court, Redis). Le front admin
// ouvre la pro frontend sur /impersonate?code=… qui échange le code contre le token.
// READ_ONLY est ouvert à SUPPORT/OPS ; FULL réservé SUPER_ADMIN/PROPELSIA_ADMIN
// (revérifié côté service via IMPERSONATE_WRITE).
// =====================================================================

export const AdminImpersonateBodySchema = Type.Object({
  scope: Type.Enum(ImpersonationScope),
});

export const AdminImpersonateResponseSchema = Type.Object({
  /** Code à usage unique à passer à la pro frontend (/impersonate?code=…). */
  code: Type.String(),
});

export const ImpersonateExchangeBodySchema = Type.Object({
  code: Type.String({ minLength: 1, maxLength: 200 }),
});

export const ImpersonateExchangeResponseSchema = Type.Object({
  /** Access token pro d'impersonation (signé JWT_KEY, non-refreshable). */
  token: Type.String(),
});

// =====================================================================
// Domain activation supervision — GET/POST /app/admin/domains
// =====================================================================
// `AdminDomainMailboxSchema` vit plus haut (section site vitrine) : il est partagé avec
// `AdminProfessionalWebsiteDomainSchema`, déclaré avant cette section.

export const AdminDomainListItemSchema = Type.Object({
  pro_website_domain_id: Type.Number(),
  pro_website_id: Type.Number(),
  domain: Type.String(),
  is_primary: Type.Boolean(),
  activation_status: Type.String(),
  dns_status: Type.String(),
  provider: Type.Optional(Type.String()),
  provider_order_id: Type.Optional(Type.String()),
  professional_id: Type.Optional(Type.Number()),
  professional_name: Type.Optional(Type.String()),
  professional_slug: Type.Optional(Type.String()),
  // Hébergement email : absent (undefined) quand le domaine n'est pas provisionné chez Stalwart.
  mail_domain_status: Type.Optional(Type.String()),
  mailbox_count: Type.Number(),
  mailbox_active_count: Type.Number(),
  search_engines: AdminSearchEnginesSchema,
  created_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'created_at', with_time: true } }),
  updated_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'updated_at', with_time: true } }),
});

/** Filtre `search_engine_status` de la liste des domaines : au moins un moteur ni vérifié ni sans objet. */
export const ADMIN_DOMAIN_SEARCH_ENGINE_FILTER_MISSING = 'missing';

export const AdminDomainListResponseSchema = Type.Object({
  items: Type.Array(AdminDomainListItemSchema),
  total: Type.Number(),
  page: Type.Number(),
  per_page: Type.Number(),
});

export const AdminDomainDetailSchema = Type.Object({
  pro_website_domain_id: Type.Number(),
  pro_website_id: Type.Number(),
  domain: Type.String(),
  is_primary: Type.Boolean(),
  activation_status: Type.String(),
  dns_status: Type.String(),
  provider: Type.Optional(Type.String()),
  provider_order_id: Type.Optional(Type.String()),
  transfer_auth_code: Type.Optional(Type.String()),
  professional_id: Type.Optional(Type.Number()),
  professional_name: Type.Optional(Type.String()),
  professional_slug: Type.Optional(Type.String()),
  k8s_ingress_name: Type.String(),
  k8s_middleware_name: Type.String(),
  k8s_secret_name: Type.String(),
  certificate_ready: Type.Boolean(),
  // Hébergement email : absent (undefined) quand le domaine n'est pas provisionné chez Stalwart.
  mail_domain_status: Type.Optional(Type.String()),
  mail_last_error: Type.Optional(Type.String()),
  mail_verified_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'mail_verified_at', with_time: true } }),
  ),
  mailboxes: Type.Array(AdminDomainMailboxSchema),
  search_engines: AdminSearchEnginesSchema,
  created_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'created_at', with_time: true } }),
  updated_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'updated_at', with_time: true } }),
});

/** Compteurs d'enrôlement par moteur (tous hôtes suivis : domaines custom, sous-domaines, Propelsia). */
export const AdminSearchEngineStatsSchema = Type.Object({
  verified: Type.Number(),
  failed: Type.Number(),
  /** Hôtes suivis ni vérifiés ni sans objet (NONE / PENDING / ADDED / FAILED). */
  missing: Type.Number(),
});

export const AdminDomainStatsResponseSchema = Type.Object({
  by_status: Type.Record(Type.String(), Type.Number()),
  total: Type.Number(),
  online: Type.Number(),
  failed: Type.Number(),
  manual_action: Type.Number(),
  // Domaines ni en ligne, ni en échec, ni en attente d'action manuelle (achat/DNS en cours) :
  // sans ce compteur, les tuiles n'expliquent pas l'écart avec le total.
  in_progress: Type.Number(),
  mail_domains_active: Type.Number(),
  mailboxes_total: Type.Number(),
  search_engines: Type.Object({
    gsc: AdminSearchEngineStatsSchema,
    bing: AdminSearchEngineStatsSchema,
  }),
});

export const AdminDomainActionResponseSchema = Type.Object({
  success: Type.Boolean(),
});

// =====================================================================
// Migration v1 → v2 : dashboard admin (visu migration + toggle du redirect legacy par pro).
//   GET  /app/admin/migration-redirect            → liste des pros migrés + statut
//   POST /app/admin/migration-redirect/:v2ProId   → (dés)active le redirect legacy
// =====================================================================

export const AdminMigrationRedirectListItemSchema = Type.Object({
  professional_id: Type.Number(),
  name: Type.String(),
  slug: Type.String(),
  is_migrated: Type.Boolean(),
  // Fin de la migration BETA (import self-serve terminé, v1 encore maître).
  beta_migrated_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'v1_beta_migrated_at', with_time: true } }),
  ),
  // Cutover FINAL (v2 maître) — null tant que la bascule n'a pas été exécutée.
  migration_completed_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'v1_migration_completed_at', with_time: true } }),
  ),
  // Redirect legacy actif (lu côté v1 : `professional.v2_pro_id IS NOT NULL`).
  redirect_active: Type.Boolean(),
  // Domaine perso (si présent) : hostname + statut d'activation v2 + en ligne (HTTPS émis).
  custom_domain: Type.Optional(Type.String()),
  custom_domain_status: Type.Optional(Type.String()),
  custom_domain_online: Type.Boolean(),
});

export const AdminMigrationRedirectListResponseSchema = Type.Object({
  items: Type.Array(AdminMigrationRedirectListItemSchema),
  total: Type.Number(),
  page: Type.Number(),
  per_page: Type.Number(),
});

// =====================================================================
// Migration v1 → v2 : bascule finale (page admin /migration-cutover).
//   POST /app/admin/migration-cutover/:v2ProId/execute   → wipe + réimport sélectif par bloc
//   GET  /app/admin/migration-cutover/:v2ProId/status    → statut du run (réutilise MigrationV1StatusResponse)
//   POST /app/admin/migration-cutover/:v2ProId/complete  → pose v1_migration_completed_at (cutover)
// (cf. docs/to_implement/migration-beta-to-final-cutover-plan.md §6bis)
// =====================================================================

export const AdminMigrationCutoverExecuteBodySchema = Type.Object({
  /** Blocs à migrer (wipe + réimport). Dépendances FORCÉES côté UI et re-validées
   * côté backend : `gift_cards_payments` ⇒ customers + catalog + appointments. */
  blocks: Type.Array(MigrationV1BlockSchema, { minItems: 1 }),
  /** false = mode arbitrage (« le pro a du contenu Propelsia à conserver ») :
   * import incrémental SANS wipe préalable — traitement manuel des conflits. */
  wipe: Type.Boolean(),
});
export type AdminMigrationCutoverExecuteBody = Static<typeof AdminMigrationCutoverExecuteBodySchema>;

export const AdminMigrationCutoverExecuteResponseSchema = Type.Object({
  accepted: Type.Boolean(),
  run_id: Type.String(),
  /** Compteurs de lignes supprimées par table (vide si wipe=false). */
  deleted_counts: Type.Record(Type.String(), Type.Number()),
});
export type AdminMigrationCutoverExecuteResponse = Static<typeof AdminMigrationCutoverExecuteResponseSchema>;

/**
 * Rattrapage post-bascule : réimport ADDITIF (jamais de wipe) d'un ou plusieurs
 * blocs sur des pros déjà migrés, y compris ceux dont la bascule finale est
 * clôturée. Sert à combler les trous d'un import passé (ex. pagination v1
 * tronquée sur les cartes cadeaux). Idempotent : chaque bloc saute les entités
 * déjà présentes dans `migration_id_map`.
 */
export const MIGRATION_V1_CATCHUP_BLOCKS = ['customers', 'gift_cards_payments', 'marketing'] as const;
export type MigrationV1CatchupBlock = (typeof MIGRATION_V1_CATCHUP_BLOCKS)[number];
export const MigrationV1CatchupBlockSchema = Type.Union([
  Type.Literal('customers'),
  Type.Literal('gift_cards_payments'),
  Type.Literal('marketing'),
]);

export const AdminMigrationCutoverCatchupBodySchema = Type.Object({
  /** Pros v2 ciblés. Absent = tous les pros migrés depuis v1 (id_map `professional`). */
  v2_professional_ids: Type.Optional(Type.Array(Type.Integer({ minimum: 1 }), { minItems: 1 })),
  /** Blocs additifs à rejouer. Défaut : `['gift_cards_payments']`. */
  blocks: Type.Optional(Type.Array(MigrationV1CatchupBlockSchema, { minItems: 1 })),
  /** true = ne lance rien, renvoie seulement la liste des pros qui seraient traités. */
  dry_run: Type.Optional(Type.Boolean()),
});
export type AdminMigrationCutoverCatchupBody = Static<typeof AdminMigrationCutoverCatchupBodySchema>;

export const AdminMigrationCutoverCatchupResponseSchema = Type.Object({
  accepted: Type.Boolean(),
  dry_run: Type.Boolean(),
  blocks: Type.Array(MigrationV1CatchupBlockSchema),
  /** Pros retenus (dans l'ordre de traitement, séquentiel). */
  v2_professional_ids: Type.Array(Type.Number()),
  /** Pros demandés mais écartés (introuvables ou sans mapping v1). */
  skipped: Type.Array(Type.Object({ v2_professional_id: Type.Number(), reason: Type.String() })),
});
export type AdminMigrationCutoverCatchupResponse = Static<typeof AdminMigrationCutoverCatchupResponseSchema>;

/**
 * « Créer + migrer » en un geste : le compte v2 est créé depuis le profil v1 (session
 * mintée par le secret partagé, sans les identifiants du pro), marqué migré dès sa
 * création (aucun email de bienvenue, aucune relance d'essai), puis l'import complet
 * est lancé. La bascule finale (`complete`) reste un acte séparé de l'admin.
 */
export const AdminMigrationCreateFromV1BodySchema = Type.Object({
  /** Identifiant du pro côté Cfixé v1 (`professional.id`). */
  v1_pro_id: Type.Integer({ minimum: 1 }),
  /** Email de connexion du compte v2. Par défaut, l'email du profil v1. */
  email: Type.Optional(Type.String({ format: 'email', maxLength: 255 })),
});
export type AdminMigrationCreateFromV1Body = Static<typeof AdminMigrationCreateFromV1BodySchema>;

export const AdminMigrationCreateFromV1ResponseSchema = Type.Object({
  professional_id: Type.Integer(),
  account_id: Type.Integer(),
  name: Type.String(),
  email: Type.String(),
  /** Jours d'essai accordés (cf. `MIGRATED_V1_TRIAL_DAYS`). */
  trial_days: Type.Integer(),
  /** Run d'import lancé dans la foulée (tous les blocs). */
  run_id: Type.String(),
});
export type AdminMigrationCreateFromV1Response = Static<typeof AdminMigrationCreateFromV1ResponseSchema>;

/** Résultat de la migration automatique d'un domaine v1 détecté au cutover (best-effort par domaine). */
export const AdminMigrationCutoverDomainResultSchema = Type.Object({
  domain: Type.String(),
  is_primary: Type.Boolean(),
  created: Type.Boolean(),
  pro_website_domain_id: Type.Union([Type.Integer(), Type.Null()]),
  /** Non-null si le domaine n'a pas pu être migré (repoint OVH KO, …) — à traiter manuellement. */
  error: Type.Union([Type.String(), Type.Null()]),
});
export type AdminMigrationCutoverDomainResult = Static<typeof AdminMigrationCutoverDomainResultSchema>;

/** Reprise automatique « Réserver avec Google » au cutover (best-effort, jamais bloquante).
 * `carried` = le pro était dans le feed v1 et son statut v2 est désormais APPROVED. */
export const AdminMigrationCutoverGoogleReserveResultSchema = Type.Object({
  carried: Type.Boolean(),
  /** true = au moins un lieu est opt-in côté v2 à l'issue du cutover. */
  place_enabled: Type.Boolean(),
  /** Non-null quand rien n'a été repris (NOT_IN_V1_FEED, STATUS_REJECTED/DISABLED, CARRY_FAILED…). */
  skipped_reason: Type.Union([Type.String(), Type.Null()]),
});
export type AdminMigrationCutoverGoogleReserveResult = Static<typeof AdminMigrationCutoverGoogleReserveResultSchema>;

export const AdminMigrationCutoverCompleteResponseSchema = Type.Object({
  completed_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'completed_at', with_time: true } }),
  /** true = le site v2 est publié à l'issue du cutover (publié ici ou déjà publié avant). */
  website_published: Type.Boolean(),
  /** Non-null quand la publication a été sautée (NO_DRAFT_CONFIG…) — le cutover est clôturé quand même. */
  website_publish_skipped_reason: Type.Union([Type.String(), Type.Null()]),
  /** Domaines v1 détectés (migration-export) et migrés automatiquement. La facturation
   * (renouvellement + annulation Stripe v1) reste attachée à part via la page Migration abonnement. */
  domains: Type.Array(AdminMigrationCutoverDomainResultSchema),
  /** Reprise « Réserver avec Google » depuis le flag feed v1 (should_sync_redirect_google). */
  google_reserve: AdminMigrationCutoverGoogleReserveResultSchema,
});
export type AdminMigrationCutoverCompleteResponse = Static<typeof AdminMigrationCutoverCompleteResponseSchema>;

export const AdminMigrationRedirectToggleBodySchema = Type.Object({
  enabled: Type.Boolean(),
});

export const AdminMigrationRedirectToggleResponseSchema = Type.Object({
  success: Type.Boolean(),
  redirect_active: Type.Boolean(),
});

// =====================================================================
// Activity catalog (métiers) — admin CRUD :
//   GET/POST/PATCH/DELETE /app/admin/activity-catalog/categories(/:id)
//   GET/POST/PATCH/DELETE /app/admin/activity-catalog/activities(/:id)
//
// Le référentiel (catégories + activités) est édité ici par l'équipe interne.
// Suppression d'activité = soft-delete (`deleted_at`) avec garde-fou sur les
// `professional_activity` rattachées. Suppression de catégorie = hard-delete
// bloquée tant qu'elle référence des activités.
// =====================================================================

/** Secteur validé contre la liste partagée `ONBOARDING_SECTOR_VALUES` (DRY). */
export const AdminActivitySectorSchema = Type.Unsafe<OnboardingSector>({
  type: 'string',
  enum: [...ONBOARDING_SECTOR_VALUES],
});

const ActivitySlugSchema = Type.String({ minLength: 1, maxLength: 120, pattern: '^[a-z0-9-]+$' });
const CategorySlugSchema = Type.String({ minLength: 1, maxLength: 80, pattern: '^[a-z0-9-]+$' });
const SeoLabelValueSchema = Type.String({ maxLength: 140 });
const SchemaOrgTypeValueSchema = Type.String({ maxLength: 80 });
const SortOrderValueSchema = Type.Integer({ minimum: 0 });
const IconEmojiValueSchema = Type.String({ maxLength: 10 });
const SeoLabelSchema = Type.Optional(Type.Union([SeoLabelValueSchema, Type.Null()]));
const IconEmojiSchema = Type.Union([IconEmojiValueSchema, Type.Null()]);

// --- Catégories ---

export const AdminActivityCategorySchema = Type.Object({
  activity_category_id: Type.Integer(),
  slug: Type.String({ maxLength: 80 }),
  default_label: Type.String({ maxLength: 120 }),
  sector: Type.String({ maxLength: 60 }),
  display_order: Type.Integer(),
  icon_emoji: IconEmojiSchema,
  is_active: Type.Boolean(),
  /** Nombre d'activités non supprimées rattachées (garde-fou suppression). */
  activity_count: Type.Integer({ minimum: 0 }),
});

export const AdminActivityCategoryListResponseSchema = Type.Object({
  items: Type.Array(AdminActivityCategorySchema),
});

export const AdminActivityCategoryCreateBodySchema = Type.Object({
  slug: CategorySlugSchema,
  default_label: Type.String({ minLength: 1, maxLength: 120 }),
  sector: AdminActivitySectorSchema,
  display_order: Type.Optional(Type.Integer({ minimum: 0 })),
  icon_emoji: Type.Optional(IconEmojiSchema),
  is_active: Type.Optional(Type.Boolean()),
});

/** Le slug d'une catégorie est immuable (clé stable du référentiel). */
export const AdminActivityCategoryUpdateBodySchema = Type.Object({
  default_label: Type.Optional(Type.String({ minLength: 1, maxLength: 120 })),
  sector: Type.Optional(AdminActivitySectorSchema),
  display_order: Type.Optional(Type.Integer({ minimum: 0 })),
  icon_emoji: clearableOnUpdate(IconEmojiValueSchema),
  is_active: Type.Optional(Type.Boolean()),
});

// --- Activités ---

export const AdminActivitySchema = Type.Object({
  activity_id: Type.Integer(),
  category_id: Type.Integer(),
  category_slug: Type.String({ maxLength: 80 }),
  category_label: Type.String({ maxLength: 120 }),
  slug: Type.String({ maxLength: 120 }),
  default_label: Type.String({ maxLength: 120 }),
  seo_label_with_article: Type.Union([Type.String({ maxLength: 140 }), Type.Null()]),
  seo_label_plural: Type.Union([Type.String({ maxLength: 140 }), Type.Null()]),
  seo_label_feminine: Type.Union([Type.String({ maxLength: 140 }), Type.Null()]),
  seo_label_practice: Type.Union([Type.String({ maxLength: 140 }), Type.Null()]),
  schema_org_type: Type.Union([Type.String({ maxLength: 80 }), Type.Null()]),
  synonyms: Type.Array(Type.String({ maxLength: 120 })),
  popularity_score: Type.Integer(),
  sort_order: Type.Union([Type.Integer(), Type.Null()]),
  is_active: Type.Boolean(),
  /** true si soft-deletée (`deleted_at` non nul). */
  is_deleted: Type.Boolean(),
  /** Nombre de pros utilisant cette activité (garde-fou suppression). */
  pro_usage_count: Type.Integer({ minimum: 0 }),
  deleted_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'deleted_at', with_time: true } }),
  ),
});

export const AdminActivityListResponseSchema = Type.Object({
  items: Type.Array(AdminActivitySchema),
  total: Type.Number(),
  page: Type.Number(),
  per_page: Type.Number(),
});

export const AdminActivityCreateBodySchema = Type.Object({
  category_id: Type.Integer({ minimum: 1 }),
  slug: ActivitySlugSchema,
  default_label: Type.String({ minLength: 1, maxLength: 120 }),
  seo_label_with_article: SeoLabelSchema,
  seo_label_plural: SeoLabelSchema,
  seo_label_feminine: SeoLabelSchema,
  seo_label_practice: SeoLabelSchema,
  schema_org_type: Type.Optional(Type.Union([SchemaOrgTypeValueSchema, Type.Null()])),
  synonyms: Type.Optional(Type.Array(Type.String({ minLength: 1, maxLength: 120 }))),
  popularity_score: Type.Optional(Type.Integer({ minimum: 0 })),
  sort_order: Type.Optional(Type.Union([SortOrderValueSchema, Type.Null()])),
  is_active: Type.Optional(Type.Boolean()),
});

/** Le slug d'une activité est immuable (clé stable). category_id réassignable. */
export const AdminActivityUpdateBodySchema = Type.Object({
  category_id: Type.Optional(Type.Integer({ minimum: 1 })),
  default_label: Type.Optional(Type.String({ minLength: 1, maxLength: 120 })),
  seo_label_with_article: clearableOnUpdate(SeoLabelValueSchema),
  seo_label_plural: clearableOnUpdate(SeoLabelValueSchema),
  seo_label_feminine: clearableOnUpdate(SeoLabelValueSchema),
  seo_label_practice: clearableOnUpdate(SeoLabelValueSchema),
  schema_org_type: clearableOnUpdate(SchemaOrgTypeValueSchema),
  synonyms: Type.Optional(Type.Array(Type.String({ minLength: 1, maxLength: 120 }))),
  popularity_score: Type.Optional(Type.Integer({ minimum: 0 })),
  sort_order: clearableOnUpdate(SortOrderValueSchema),
  is_active: Type.Optional(Type.Boolean()),
});

export const AdminActivityActionResponseSchema = Type.Object({
  success: Type.Boolean(),
});

// =====================================================================
// Journal d'audit — GET /app/admin/audit-logs (lecture seule, filtrable)
// =====================================================================

export const AdminAuditLogActorSchema = Type.Object({
  admin_user_id: Type.Number(),
  name: Type.String(),
});

export const AdminAuditLogListItemSchema = Type.Object({
  admin_audit_log_id: Type.Number(),
  action: Type.Enum(AdminAuditAction),
  actor_admin_user_id: Type.Number(),
  actor_name: Type.String(),
  actor_email: Type.String(),
  target_kind: Type.Optional(Type.String()),
  target_id: Type.Optional(Type.String()),
  // before/after sont des snapshots JSON libres (forme dépendante de l'action) → passe-through.
  before: Type.Optional(Type.Unknown()),
  after: Type.Optional(Type.Unknown()),
  ip: Type.Optional(Type.String()),
  user_agent: Type.Optional(Type.String()),
  at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'at', with_time: true } }),
});

export const AdminAuditLogListResponseSchema = Type.Object({
  items: Type.Array(AdminAuditLogListItemSchema),
  total: Type.Number(),
  page: Type.Number(),
  per_page: Type.Number(),
  // Liste des acteurs ayant des entrées d'audit — alimente le filtre côté front.
  actors: Type.Array(AdminAuditLogActorSchema),
});

// =====================================================================
// Gestion des utilisateurs admin — /app/admin/admin-users (§9, scope ADMIN_USERS_MANAGE)
// =====================================================================

/**
 * Rôles assignables via l'UI (PROPELSIA_ADMIN legacy exclu — non proposé à la création).
 * Source unique : le schéma de validation ET le `<select>` de la page admin-users en dérivent,
 * pour qu'un nouveau rôle n'ait pas à être déclaré à deux endroits.
 */
export const ASSIGNABLE_ADMIN_ROLE_VALUES = [
  AdminRole.SUPER_ADMIN,
  AdminRole.FINANCE,
  AdminRole.SUPPORT,
  AdminRole.OPS,
  AdminRole.READ_ONLY,
  AdminRole.INFOGERANCE,
  AdminRole.COMMUNICATION,
] as const;

const ASSIGNABLE_ADMIN_ROLES = ASSIGNABLE_ADMIN_ROLE_VALUES.map(
  (role): ReturnType<typeof Type.Literal<typeof role>> => Type.Literal(role),
);

export const AdminUserListItemSchema = Type.Object({
  admin_user_id: Type.Number(),
  email: Type.String(),
  name: Type.String(),
  role: Type.Enum(AdminRole),
  is_active: Type.Boolean(),
  mfa_enabled: Type.Boolean(),
  last_login_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'last_login_at', with_time: true } }),
  ),
  created_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'created_at', with_time: true } }),
  /** Ligne téléphonique (compte de facturation OVH + ligne SIP) : appel en un clic depuis l'admin. */
  telephony_account: Type.Optional(Type.String()),
  telephony_line: Type.Optional(Type.String()),
});

export const AdminUserListResponseSchema = Type.Object({
  items: Type.Array(AdminUserListItemSchema),
  /** La connexion au fournisseur téléphonique est configurée sur ce serveur (sinon les lignes sont inactives). */
  telephony_available: Type.Optional(Type.Boolean()),
});

export const AdminUserCreateBodySchema = Type.Object({
  email: Type.String({ format: 'email', maxLength: 255 }),
  name: Type.String({ minLength: 1, maxLength: 255 }),
  role: Type.Union(ASSIGNABLE_ADMIN_ROLES),
  // Mot de passe défini à la création par le SUPER_ADMIN (décision §11.12 — pas de flux email v1).
  password: Type.String({ minLength: 12, maxLength: 255 }),
});

export const AdminUserUpdateBodySchema = Type.Object({
  name: Type.Optional(Type.String({ minLength: 1, maxLength: 255 })),
  role: Type.Optional(Type.Union(ASSIGNABLE_ADMIN_ROLES)),
  is_active: Type.Optional(Type.Boolean()),
  /** Les deux ensemble ; chaîne vide = ligne retirée (retour au lien `tel:`). */
  telephony_account: Type.Optional(Type.String({ maxLength: 64 })),
  telephony_line: Type.Optional(Type.String({ maxLength: 32 })),
});

export const AdminUserResetPasswordBodySchema = Type.Object({
  password: Type.String({ minLength: 12, maxLength: 255 }),
});

// =====================================================================
// Static type exports
// =====================================================================

export type AdminLoginBody = Static<typeof AdminLoginBodySchema>;
export type AdminLoginResponse = Static<typeof AdminLoginResponseSchema>;
export type AdminLoginStepResponse = Static<typeof AdminLoginStepResponseSchema>;
export type AdminMfaTokenBody = Static<typeof AdminMfaTokenBodySchema>;
export type AdminMfaEnrollStartResponse = Static<typeof AdminMfaEnrollStartResponseSchema>;
export type AdminMfaCodeBody = Static<typeof AdminMfaCodeBodySchema>;
export type AdminMfaEnrollConfirmResponse = Static<typeof AdminMfaEnrollConfirmResponseSchema>;
export type AdminLeadListItem = Static<typeof AdminLeadListItemSchema>;
export type AdminLeadListResponse = Static<typeof AdminLeadListResponseSchema>;
export type AdminLeadDetail = Static<typeof AdminLeadDetailSchema>;
export type AdminLeadUpdateBody = Static<typeof AdminLeadUpdateBodySchema>;
export type AdminToolInvocation = Static<typeof AdminToolInvocationSchema>;
export type AdminConversationMessage = Static<typeof AdminConversationMessageSchema>;
export type AdminLeadConversationResponse = Static<typeof AdminLeadConversationResponseSchema>;
export type AdminProfile = Static<typeof AdminProfileSchema>;
export type AdminDashboardStatsResponse = Static<typeof AdminDashboardStatsResponseSchema>;
export type AdminProfessionalListItem = Static<typeof AdminProfessionalListItemSchema>;
export type AdminProfessionalListResponse = Static<typeof AdminProfessionalListResponseSchema>;
export type AdminSegmentProrataCharge = Static<typeof AdminSegmentProrataChargeSchema>;
export type AdminSubscriptionSegment = Static<typeof AdminSubscriptionSegmentSchema>;
export type AdminProfessionalSubscription = Static<typeof AdminProfessionalSubscriptionSchema>;
export type AdminProfessionalQuota = Static<typeof AdminProfessionalQuotaSchema>;
export type AdminProfessionalVat = Static<typeof AdminProfessionalVatSchema>;
export type AdminProfessionalDetail = Static<typeof AdminProfessionalDetailSchema>;
export type AdminProfessionalReferral = Static<typeof AdminProfessionalReferralSchema>;
export type AdminProfessionalWebsiteDomain = Static<typeof AdminProfessionalWebsiteDomainSchema>;
export type AdminProfessionalWebsite = Static<typeof AdminProfessionalWebsiteSchema>;
export type AdminProfessionalTrialUpdateBody = Static<typeof AdminProfessionalTrialUpdateBodySchema>;
export type AdminCalendarSyncConnection = Static<typeof AdminCalendarSyncConnectionSchema>;
export type AdminCalendarSyncConnectionsResponse = Static<typeof AdminCalendarSyncConnectionsResponseSchema>;
export type AdminCalendarSyncReconcileBody = Static<typeof AdminCalendarSyncReconcileBodySchema>;
export type AdminCalendarSyncReconcileResponse = Static<typeof AdminCalendarSyncReconcileResponseSchema>;
export type AdminSubscriptionSegmentsRegenerateBody = Static<typeof AdminSubscriptionSegmentsRegenerateBodySchema>;
export type AdminProfessionalSubscriptionUpdateBody = Static<typeof AdminProfessionalSubscriptionUpdateBodySchema>;
export type AdminImpersonateBody = Static<typeof AdminImpersonateBodySchema>;
export type AdminImpersonateResponse = Static<typeof AdminImpersonateResponseSchema>;
export type ImpersonateExchangeBody = Static<typeof ImpersonateExchangeBodySchema>;
export type ImpersonateExchangeResponse = Static<typeof ImpersonateExchangeResponseSchema>;
export type AdminDomainMailbox = Static<typeof AdminDomainMailboxSchema>;
export type AdminSearchEngineProperty = Static<typeof AdminSearchEnginePropertySchema>;
export type AdminSearchEngines = Static<typeof AdminSearchEnginesSchema>;
export type AdminSearchEngineStats = Static<typeof AdminSearchEngineStatsSchema>;
export type AdminDomainListItem = Static<typeof AdminDomainListItemSchema>;
export type AdminDomainListResponse = Static<typeof AdminDomainListResponseSchema>;
export type AdminDomainDetail = Static<typeof AdminDomainDetailSchema>;
export type AdminDomainStatsResponse = Static<typeof AdminDomainStatsResponseSchema>;
export type AdminDomainActionResponse = Static<typeof AdminDomainActionResponseSchema>;
export type AdminMigrationRedirectListItem = Static<typeof AdminMigrationRedirectListItemSchema>;
export type AdminMigrationRedirectListResponse = Static<typeof AdminMigrationRedirectListResponseSchema>;
export type AdminMigrationRedirectToggleBody = Static<typeof AdminMigrationRedirectToggleBodySchema>;
export type AdminMigrationRedirectToggleResponse = Static<typeof AdminMigrationRedirectToggleResponseSchema>;
export type AdminActivityCategory = Static<typeof AdminActivityCategorySchema>;
export type AdminActivityCategoryListResponse = Static<typeof AdminActivityCategoryListResponseSchema>;
export type AdminActivityCategoryCreateBody = Static<typeof AdminActivityCategoryCreateBodySchema>;
export type AdminActivityCategoryUpdateBody = Static<typeof AdminActivityCategoryUpdateBodySchema>;
export type AdminActivity = Static<typeof AdminActivitySchema>;
export type AdminActivityListResponse = Static<typeof AdminActivityListResponseSchema>;
export type AdminActivityCreateBody = Static<typeof AdminActivityCreateBodySchema>;
export type AdminActivityUpdateBody = Static<typeof AdminActivityUpdateBodySchema>;
export type AdminActivityActionResponse = Static<typeof AdminActivityActionResponseSchema>;
export type AdminAuditLogActor = Static<typeof AdminAuditLogActorSchema>;
export type AdminAuditLogListItem = Static<typeof AdminAuditLogListItemSchema>;
export type AdminAuditLogListResponse = Static<typeof AdminAuditLogListResponseSchema>;
export type AdminUserListItem = Static<typeof AdminUserListItemSchema>;
export type AdminUserListResponse = Static<typeof AdminUserListResponseSchema>;
export type AdminUserCreateBody = Static<typeof AdminUserCreateBodySchema>;
export type AdminUserUpdateBody = Static<typeof AdminUserUpdateBodySchema>;
export type AdminUserResetPasswordBody = Static<typeof AdminUserResetPasswordBodySchema>;

// =====================================================================
// Horloge virtuelle — /app/admin/clock/* (outil de test preprod, neutralisé en prod)
// =====================================================================

/** Crons de facturation déclenchables manuellement via l'horloge. */
export enum AdminClockCronJob {
  BILLING_RENEWALS = 'billing_renewals',
  BILLING_RETRIES = 'billing_retries',
  /** Relance des essais : passage horaire (moments du jour à 10h locale + SMS différés). */
  TRIAL_RELANCE_HOURLY = 'trial_relance_hourly',
  /** Relance des essais : snapshot nocturne d'activité PostHog (S1 / S5). */
  TRIAL_ENGAGEMENT_NIGHTLY = 'trial_engagement_nightly',
  /** CRM : passe quotidienne d'alimentation de la file d'appels (essais terminés, leads sans réponse). */
  CRM_QUEUE_DAILY = 'crm_queue_daily',
  /** CRM : passe horaire des suites (SMS de dernière chance, suites de formation). */
  CRM_FOLLOWUP_HOURLY = 'crm_followup_hourly',
  /** CRM : envoi des campagnes programmées dont l'heure est passée. */
  CRM_CAMPAIGN_SEND = 'crm_campaign_send',
  /** CRM : relecture des statistiques des campagnes envoyées. */
  CRM_CAMPAIGN_STATS = 'crm_campaign_stats',
  /** CRM : passe horaire de la séquence automatique des inscrits non clients. */
  CRM_SEQUENCE_HOURLY = 'crm_sequence_hourly',
}

/** Mode courant de l'horloge tel qu'exposé à l'UI admin. */
export enum AdminClockMode {
  /** Temps réel (pas d'override, ou prod). */
  REAL = 'real',
  /** Temps décalé d'un offset constant (continue de s'écouler). */
  OFFSET = 'offset',
  /** Temps figé à un instant donné. */
  FROZEN = 'frozen',
}

export const AdminClockStateSchema = Type.Object({
  /** `false` en prod, ou tant qu'aucun override n'est posé. */
  enabled: Type.Boolean(),
  mode: Type.Enum(AdminClockMode),
  /** Décalage appliqué au temps réel (ms). 0 en mode réel. */
  offset_ms: Type.Number(),
  /** Instant ISO de gel, ou null si le temps n'est pas figé. */
  frozen_at: Type.Union([Type.String(), Type.Null()]),
  /** `now()` virtuel courant, ISO (ce que le moteur de facturation voit). */
  now: Type.String(),
  /** `now()` virtuel formaté pour affichage (locale/timezone ops). */
  now_formatted: Type.String(),
  /** Vrai ssi l'environnement est prod (l'UI désactive alors les contrôles). */
  is_prod: Type.Boolean(),
});

export const AdminClockSetRequestSchema = Type.Object({
  // ISO complet (avec offset/Z). Volontairement sans `format: 'date-time'` pour ne pas
  // déclencher la conversion timezone de @TypeBoxBody : on veut un instant absolu prévisible.
  date: Type.String({ minLength: 1 }),
  freeze: Type.Optional(Type.Boolean()),
});

export const AdminClockAdvanceRequestSchema = Type.Object({
  days: Type.Optional(Type.Number()),
  hours: Type.Optional(Type.Number()),
  minutes: Type.Optional(Type.Number()),
});

export const AdminClockRunCronRequestSchema = Type.Object({
  job: Type.Enum(AdminClockCronJob),
});

export const AdminClockRunCronResponseSchema = Type.Object({
  ok: Type.Boolean(),
  job: Type.Enum(AdminClockCronJob),
});

export type AdminClockState = Static<typeof AdminClockStateSchema>;
export type AdminClockSetRequest = Static<typeof AdminClockSetRequestSchema>;
export type AdminClockAdvanceRequest = Static<typeof AdminClockAdvanceRequestSchema>;
export type AdminClockRunCronRequest = Static<typeof AdminClockRunCronRequestSchema>;
export type AdminClockRunCronResponse = Static<typeof AdminClockRunCronResponseSchema>;

// =====================================================================
// Relances 1:1 — file `trigger_run` (GET /admin/relances, POST cancel/retry)
//
// `trigger_run` est écrit par les cascades Pulse (SUSPENS, silence) depuis l'origine, mais
// n'avait aucune lecture admin : une relance programmée, en retard ou en échec était invisible.
// Ces schémas ouvrent la file et les deux seules actions qui ont du sens dessus.
// =====================================================================

/** Onglets de la file — ce sont des VUES sur `TriggerRunStatus`, pas le statut lui-même. */
export enum AdminRelanceView {
  /** PENDING dont l'échéance est encore devant. */
  UPCOMING = 'UPCOMING',
  /** PENDING dont l'échéance est passée : le worker aurait dû l'envoyer. */
  LATE = 'LATE',
  SENT = 'SENT',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export const AdminRelanceItemSchema = Type.Object({
  trigger_run_id: Type.Number(),
  lead_id: Type.Number(),
  professional_id: Type.Optional(Type.Number()),
  /** Nom du pro lié, ou identité de repli du lead (email / téléphone) quand il n'y a pas de pro. */
  subject_label: Type.String(),
  conversation_id: Type.Optional(Type.Number()),
  channel: Type.String(),
  /** Clé de wording (`knowledge/relance-wordings.ts`) — l'admin veut savoir CE QUI part. */
  message_template: Type.String(),
  /** Texte réellement envoyé, figé à l'envoi. Absent tant que la relance n'est pas partie, et sur
   * les relances antérieures à la colonne. Seule source fiable depuis que le composer LLM génère
   * le wording au dispatch : `message_template` ne dit plus ce que le lead a lu. */
  sent_body: Type.Optional(Type.String()),
  /** Identifiant de cascade (`refus_preview`, `objection_prix`, …). */
  reason: Type.String(),
  status: Type.Enum(TriggerRunStatus),
  /** true quand `status = PENDING` et que l'échéance est dépassée. */
  is_late: Type.Boolean(),
  attempts: Type.Number(),
  last_error: Type.Optional(Type.String()),
  scheduled_at_formatted: Type.String({
    format: 'date-time',
    meta: { source_key: 'scheduled_at', with_time: true },
  }),
  sent_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'sent_at', with_time: true } }),
  ),
  lead_state: Type.String(),
});

export const AdminRelanceListResponseSchema = Type.Object({
  items: Type.Array(AdminRelanceItemSchema),
  total: Type.Number(),
  page: Type.Number(),
  per_page: Type.Number(),
  /** Volume par vue, pour les compteurs d'onglets — évite un aller-retour par onglet. */
  counts: Type.Record(Type.String(), Type.Number()),
});

export const AdminRelanceActionResponseSchema = Type.Object({
  trigger_run_id: Type.Number(),
  status: Type.Enum(TriggerRunStatus),
});

export type AdminRelanceItem = Static<typeof AdminRelanceItemSchema>;
export type AdminRelanceListResponse = Static<typeof AdminRelanceListResponseSchema>;
export type AdminRelanceActionResponse = Static<typeof AdminRelanceActionResponseSchema>;

// =====================================================================
// Escalades — conversations en pilotage humain (GET /admin/escalations)
//
// `conversation.pilot` existait en base et n'était lu nulle part : une conversation rendue à
// l'humain disparaissait des radars. Cette inbox est le seul endroit qui la rattrape.
// =====================================================================

export const AdminEscalationItemSchema = Type.Object({
  conversation_id: Type.Number(),
  lead_id: Type.Number(),
  professional_id: Type.Optional(Type.Number()),
  subject_label: Type.String(),
  lead_state: Type.String(),
  active_playbook: Type.Optional(Type.String()),
  /** Extrait du dernier message de la conversation — de quoi trier sans ouvrir la fiche. */
  last_message_excerpt: Type.Optional(Type.String()),
  last_message_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'last_message_at', with_time: true } }),
  ),
  /** Dernier message ENTRANT : c'est lui qui dit depuis combien de temps le lead attend. */
  last_inbound_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'last_inbound_at', with_time: true } }),
  ),
  /** Minutes écoulées depuis le dernier message entrant — sert au tri « qui attend le plus ». */
  waiting_minutes: Type.Optional(Type.Number()),
});

export const AdminEscalationListResponseSchema = Type.Object({
  items: Type.Array(AdminEscalationItemSchema),
  total: Type.Number(),
  page: Type.Number(),
  per_page: Type.Number(),
});

/** Prendre la main (`HUMAN`) ou rendre la conversation à Pulse (`BOT`). */
export const AdminEscalationSetPilotBodySchema = Type.Object({
  pilot: Type.Enum(ConversationPilot),
});

export const AdminEscalationPilotResponseSchema = Type.Object({
  conversation_id: Type.Number(),
  pilot: Type.Enum(ConversationPilot),
});

export type AdminEscalationItem = Static<typeof AdminEscalationItemSchema>;
export type AdminEscalationSetPilotBody = Static<typeof AdminEscalationSetPilotBodySchema>;
export type AdminEscalationListResponse = Static<typeof AdminEscalationListResponseSchema>;
export type AdminEscalationPilotResponse = Static<typeof AdminEscalationPilotResponseSchema>;

/* -------------------------------------------------------------------------------------------- */
/* Bascules Cfixé → Propelsia : demandes programmées par les pros depuis l'espace v1.             */
/* La donnée vit en base v1 ; l'admin la lit via les routes internes `pro_migration/internal`.    */
/* -------------------------------------------------------------------------------------------- */

/** Statut d'une demande de bascule, tel que porté par la v1 (`ProMigrationStatus`). */
export enum ProMigrationStatus {
  SCHEDULED = 'SCHEDULED',
  DONE = 'DONE',
  CANCELLED = 'CANCELLED',
}

export const AdminMigrationBasculeItemSchema = Type.Object({
  migration_request_id: Type.Number(),
  v1_pro_id: Type.Number(),
  v2_pro_id: Type.Union([Type.Number(), Type.Null()]),
  slug: Type.String(),
  establishment: Type.String(),
  contact_name: Type.String(),
  contact_email: Type.String(),
  contact_phone: Type.Union([Type.String(), Type.Null()]),
  /** `YYYY-MM-DD` — le jour de la bascule, réalisée en soirée. */
  migration_date: Type.String(),
  /** « mardi 22 septembre », formaté côté backend (règle dates du repo). */
  migration_date_label: Type.String(),
  status: Type.Enum(ProMigrationStatus),
  consents: Type.Array(Type.Object({ id: Type.String(), label: Type.String() })),
  message: Type.Union([Type.String(), Type.Null()]),
  created_at: Type.String(),
  cancelled_at: Type.Union([Type.String(), Type.Null()]),
  done_at: Type.Union([Type.String(), Type.Null()]),
  /**
   * Email de connexion du compte Propelsia rattaché (owner du pro v2), `null` tant qu'aucun compte
   * n'existe : l'email de fin de bascule ne peut pas partir sans lui (lien de mot de passe).
   */
  login_email: Type.Union([Type.String(), Type.Null()]),
  /** « 15 sept. à 21:42 », dernier envoi réussi de l'email de fin de bascule (`null` = jamais envoyé). */
  done_email_sent_at_label: Type.Union([Type.String(), Type.Null()]),
});
export type AdminMigrationBasculeItem = Static<typeof AdminMigrationBasculeItemSchema>;

export const AdminMigrationBasculeListResponseSchema = Type.Object({
  items: Type.Array(AdminMigrationBasculeItemSchema),
  total: Type.Number(),
});
export type AdminMigrationBasculeListResponse = Static<typeof AdminMigrationBasculeListResponseSchema>;

export const AdminMigrationBasculeValidateResponseSchema = Type.Object({
  item: AdminMigrationBasculeItemSchema,
});
export type AdminMigrationBasculeValidateResponse = Static<typeof AdminMigrationBasculeValidateResponseSchema>;

/**
 * Envoi manuel de l'email de fin de bascule, découplé de la validation : la bascule DNS peut prendre
 * jusqu'à une heure, l'équipe envoie l'email quand le domaine répond.
 */
export const AdminMigrationBasculeSendEmailResponseSchema = Type.Object({
  item: AdminMigrationBasculeItemSchema,
  /**
   * `true` = l'email de fin de bascule est dans la file d'envoi (l'envoi Mailjet est asynchrone).
   * `false` = rien n'a pu être mis en file (adresse absente, file indisponible) : l'admin peut réessayer.
   */
  email_sent: Type.Boolean(),
});
export type AdminMigrationBasculeSendEmailResponse = Static<typeof AdminMigrationBasculeSendEmailResponseSchema>;
